/**
 * Zwischenspeicher fuer Formulareingaben (ERR-11).
 *
 * "Unerwartete Fehler fuehren nie zu Datenverlust im Formular: erfasste
 * Eingaben werden zwischengespeichert und nach Wiederherstellung zur
 * Uebernahme angeboten."
 *
 * Die Anmeldemaske ist der einfachste Fall — zwei Felder —, und trotzdem der
 * richtige Ort, um das Muster einmal zu bauen: Die spaeteren Masken haben
 * dreissig Felder, und dort ist ein verlorener Entwurf eine halbe Stunde
 * Arbeit.
 *
 * WAS HIER NICHT GESPEICHERT WIRD: Passwoerter, Bestaetigungscodes,
 * Notfallcodes, Bankverbindungen. Ein Zwischenspeicher, der ein Passwort im
 * Klartext ablegt, ist ein Schaden und keine Hilfe — er ueberlebt den Tab und
 * den naechsten Benutzer am selben Rechner.
 *
 * Die Auswahl steht hier und nicht beim Aufrufer, aber sie ist eine
 * SPERRLISTE und keine Positivliste: Was nicht auf sie passt, wird
 * gespeichert. Das ist die schwaechere Bauart — ein neues Feld mit einem
 * Namen, an den niemand gedacht hat, rutscht durch. Fuer die Anmeldemaske mit
 * zwei Feldern traegt sie; sobald die erste Fachmaske einen Zwischenspeicher
 * bekommt, gehoert sie auf eine ausdrueckliche Positivliste je Maske
 * umgestellt (ERR-12, SYS-03: Bankdaten und Kreditlimit sind feldbezogen
 * geschuetzt und haben in keinem Zwischenspeicher etwas verloren).
 *
 * sessionStorage und nicht localStorage: Ein Entwurf gehoert zum Tab, in dem
 * er entstand. Auf einem gemeinsam genutzten Rechner soll der naechste
 * Benutzer nicht die Eingaben des vorherigen vorfinden.
 */

const PRAEFIX = 'crm.entwurf.';

/** Feldnamen, die NIE zwischengespeichert werden. */
const NIEMALS =
  /(passwort|password|kennwort|code|geheimnis|secret|token|schluessel|key|pin|iban|bic|kontonummer|bankverbindung|kreditlimit|einkaufspreis|deckungsbeitrag)/i;

export type Entwurf = Readonly<Record<string, string>>;

export function bewahre(maske: string, werte: Entwurf): void {
  const unbedenklich = Object.fromEntries(
    Object.entries(werte).filter(([feld]) => !NIEMALS.test(feld)),
  );

  // Ein leerer Entwurf ist kein Entwurf. Ihn zu speichern hiesse, beim
  // naechsten Aufruf ein leeres Formular "wiederherzustellen".
  if (Object.values(unbedenklich).every((wert) => wert.trim() === '')) {
    verwirf(maske);
    return;
  }

  try {
    sessionStorage.setItem(PRAEFIX + maske, JSON.stringify(unbedenklich));
  } catch {
    // Voller oder gesperrter Speicher. Der Entwurf geht dann verloren — das
    // ist schlechter als mit Zwischenspeicher und besser als ein Absturz.
  }
}

export function hole(maske: string): Entwurf {
  try {
    const roh = sessionStorage.getItem(PRAEFIX + maske);
    if (roh === null) return {};
    const geparst: unknown = JSON.parse(roh);
    if (typeof geparst !== 'object' || geparst === null) return {};
    // Nur Zeichenketten uebernehmen: Was sonst in der Ablage stand, kam nicht
    // von hier.
    return Object.fromEntries(
      Object.entries(geparst as Record<string, unknown>).filter(
        ([feld, wert]) => typeof wert === 'string' && !NIEMALS.test(feld),
      ),
    ) as Entwurf;
  } catch {
    return {};
  }
}

export function verwirf(maske: string): void {
  try {
    sessionStorage.removeItem(PRAEFIX + maske);
  } catch {
    /* siehe bewahre */
  }
}

/** Beim Abmelden gehen alle Entwuerfe mit — sie gehoeren zur Sitzung. */
export function verwirfAlle(): void {
  try {
    for (const schluessel of Object.keys(sessionStorage)) {
      if (schluessel.startsWith(PRAEFIX)) sessionStorage.removeItem(schluessel);
    }
  } catch {
    /* siehe bewahre */
  }
}
